import { getApiConfig } from '../config/markets.js';

const API_URL = getApiConfig().apiUrl;

/**
 * Enviar un mensaje como agente:
 * - Verificar que la conversación exista y esté en estado 'agent_assigned'.
 * - Llamar a WhatsApp Business API para enviar el texto.
 * - Guardar el mensaje en la BD con from='agent'.
 * - Emitir vía Socket.io el nuevo mensaje a la sala de esa conversación.
 */

export async function sendMessageAsAgent(conversationId, text) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No se encontró token de autenticación');
  }

  const res = await fetch(`${API_URL}/messages/${conversationId}/send`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ body: text }),
  });

  if (!res.ok) {
    let errMsg = 'Error al enviar mensaje';
    try {
      const data = await res.json();
      errMsg = data.error || data.message || errMsg;
    } catch {}
    throw new Error(errMsg);
  }

  return await res.json();
}



/**
 * Enviar un documento como agente:
 * - Sube el archivo al servidor
 * - El servidor lo envía a WhatsApp y lo guarda en el bucket
 * - Guarda el mensaje en la BD
 * - Emite el nuevo mensaje vía Socket.io
 */
export async function sendDocumentAsAgent(conversationId, file, caption = '') {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No se encontró token de autenticación');
  }

  const formData = new FormData();
  formData.append('document', file);
  if (caption) {
    formData.append('caption', caption);
  }

  const res = await fetch(`${API_URL}/messages/${conversationId}/send-document`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      // No incluir Content-Type, el navegador lo configurará automáticamente con boundary
    },
    body: formData,
  });

  if (!res.ok) {
    let errMsg = 'Error al enviar documento';
    try {
      const data = await res.json();
      errMsg = data.error || data.message || errMsg;
    } catch {}
    throw new Error(errMsg);
  }

  return await res.json();
}

/**
 * Enviar una nota de voz / audio como agente:
 * - Sube el blob de audio al servidor (campo `audio`).
 * - El servidor lo envía a WhatsApp (type 'audio'), lo archiva en GCS de
 *   compliance y guarda el mensaje en la BD.
 * - Emite el nuevo mensaje vía Socket.io.
 *
 * @param {string} conversationId
 * @param {Blob} audioBlob - El audio grabado (preferiblemente audio/ogg;codecs=opus).
 * @param {string} [filename] - Nombre del fichero a enviar.
 */
export async function sendAudioAsAgent(conversationId, audioBlob, filename = 'voice-note.ogg') {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No se encontró token de autenticación');
  }

  const formData = new FormData();
  formData.append('audio', audioBlob, filename);

  const res = await fetch(`${API_URL}/messages/${conversationId}/send-audio`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      // Sin Content-Type: el navegador añade el boundary del multipart.
    },
    body: formData,
  });

  if (!res.ok) {
    let errMsg = 'Error al enviar nota de voz';
    try {
      const data = await res.json();
      errMsg = data.error || data.message || errMsg;
    } catch {}
    throw new Error(errMsg);
  }

  return await res.json();
}

/**
 * Obtiene todos los mensajes de una conversación dada.
 * - conversationId: el id de la conversación en MongoDB.
 * - Añade Authorization: Bearer <token> en las cabeceras.
 * - Retorna un array con los mensajes (ordenados de más antiguo a más reciente).
 */
export async function getMessagesByConversation(conversationId) {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No se encontró token de autenticación');
    }

    const res = await fetch(`${API_URL}/messages/${conversationId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!res.ok) {
      let errMsg = 'Error al obtener mensajes';
      try {
        const data = await res.json();
        errMsg = data.message || errMsg;
      } catch {}
      throw new Error(errMsg);
    }

    const messages = await res.json();
    return messages;
  }
  
